import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { AdminBaseEditComponent } from 'src/modules/sm-base/components/admin-base-edit/admin-base-edit.component';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { FrontendFieldError } from 'src/modules/sm-base/netgen/FrontendFieldError';
import { Utils } from 'src/modules/utils/shared/utils';
import { ProjectPlanningTools } from '../../models/project-planning-tools.model';
import { CalendarEventStartRelativeTo } from '../../netgen/CalendarEventStartRelativeTo';
import { CalendarEventTemplate } from '../../netgen/CalendarEventTemplate';
import { CalendarEventType } from '../../netgen/CalendarEventType';
import { CalendarRole } from '../../netgen/CalendarRole';
import { ProjectPlanningService } from '../../services/project-planning.service';

@Component({
    selector: 'app-edit-calendar-event-template',
    templateUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.html',
    styleUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditCalendarEventTemplateComponent extends AdminBaseEditComponent<CalendarEventTemplate> {

    service = inject(ProjectPlanningService);

    definition = ProjectPlanningTools.definitionCalendarEventTemplate;

    types: CalendarEventType[] = [];
    roles: CalendarRole[] = [];
    users: DatabaseUser[];
    updatingReminderDeltaFields = false;
    
    async initAdditionalData(): Promise<void> {
        this.types = await this.service.getEventTypes();
        this.roles = await this.service.getRoles();
        this.users = await this.service.getActiveUsers();
        this.item.roles ??= [];
    }

    getForm(): FrontendForm {
        return this.app.tForm("entity.CalendarEventTemplate.fields", new FrontendForm([
            new FrontendField("title", "", FrontendFieldType.string, { 
                mandatory: true,
                maxLength: 100,
                guiGroup: "title" 
            }),
            new FrontendField("typeId", "", FrontendFieldType.dropdown, { 
                mandatory: true, 
                dropdownCanFilter: true,
                emptyValues: [null, 0],
                listItems: FrontendFieldListItem.make(this.types, item => [item.id, item.title], true),
                guiGroup: "title"
            }),
            new FrontendField("description", "", FrontendFieldType.textArea, {
                fixedHeight: "100px"
            }),
            new FrontendField("subTasks", "", FrontendFieldType.stringList),    
            new FrontendField("_1", "", FrontendFieldType.splitter),
            new FrontendField("isMilestone", "", FrontendFieldType.bool, { 
                onValueChanged: this.isMilestoneChanged.bind(this),
                callValueChangedAfterLoad: true
            }),
            new FrontendField("durationDays", "", FrontendFieldType.int, { minValue: 1 }),
            new FrontendField("_2", "", FrontendFieldType.splitter),
            new FrontendField("onlyWeekdays", "", FrontendFieldType.bool),
            new FrontendField("workingTime", "", FrontendFieldType.decimal, { 
                spinnerStep: 0.25,
                allowOnlySpinnerStepValues: true,
                validator: (field, form) => {
                    let roles = form.getValue("roles") as any[];
                    return field.getValueNumber() > 0 && roles != null && (roles.length > 1 ||
                                    roles.length == 1 && roles[0].count > 1)
                                ? [new FrontendFieldError(field.id, this.app.t("entity.CalendarEventTemplate.other.workingTimeRolesValidation"))] : [];
                        }
            }),
            new FrontendField("startRelativeDays", "", FrontendFieldType.int, {
                minValue: null, 
                guiGroup: "startRelative",
                validator: (field, form) => field.getValueNumber() < 0 && (form.getValueNumber("startRelativeTo") as CalendarEventStartRelativeTo) == CalendarEventStartRelativeTo.startDate ? [new FrontendFieldError(field.id, this.app.t("entity.CalendarEventTemplate.other.projectStartValidation"))] : []
            }),
            new FrontendField("startRelativeTo", "", FrontendFieldType.dropdown, { 
                mandatory: true, 
                guiGroup: "startRelative", 
                listItems: FrontendFieldListItem.make(Utils.getEnumValues(CalendarEventStartRelativeTo), item => [item, CalendarEventStartRelativeTo.getDisplayName(item, this.app)])
            }),                 
            new FrontendField("_3", "", FrontendFieldType.splitter),
            new FrontendField("forStandalone", "", FrontendFieldType.bool),
            new FrontendField("_4", "", FrontendFieldType.splitter),
            new FrontendField("roles", "", FrontendFieldType.table, {
                tableColumns: [
                    new TableColumn("roleId", this.app.t("entity.CalendarEventTemplateRole.fields.roleId"), TableCellType.int, 20, {
                        editable: true,
                        editor: new FrontendField("", "", FrontendFieldType.dropdown, {
                            listItems: FrontendFieldListItem.make(this.roles, item => [item.id, item.title]),
                            dropdownCanFilter: true
                        }),
                        valueFormatter: e => this.roles.find(item => item.id == e.value)?.title ?? ""
                    }),
                    new TableColumn("count", this.app.t("entity.CalendarEventTemplateRole.fields.count"), TableCellType.int, 2, {
                        editable: true
                    })
                ],
                mandatory: true,
                fixedHeight: "150px",
                tableCanAddRow: true,
                tableCanRemoveRow: true
            }),
            ...ProjectPlanningTools.createReminderFields(this.app, () => this.form),
            new FrontendField("contactPersonRoleId", "", FrontendFieldType.dropdown, { 
                listItems: FrontendFieldListItem.make(this.roles, item => [item.id, item.title], true, [[null, this.app.t("entity.CalendarEventTemplate.other.noCreator")]]),
                dropdownCanFilter: true
            })
        ]));
    }

    validateForm(): boolean {
        return this.app.validateForm(this.form, () => {
            let roles = this.form.getValue("roles") as any[];
            for (let role of roles) {
                if (role.count < 1) {
                    return [new FrontendFieldError("roles", this.app.t("entity.CalendarEventTemplate.other.roleCountValidation"))];
                }
            }
            return [];
        });        
    }

    isMilestoneChanged(): void {
        let isMilestone = this.form.getValueBool("isMilestone");
        this.form.getField("durationDays").enabled = !isMilestone;
        this.form.getField("workingTime").enabled = !isMilestone;
        if (isMilestone) {
            this.form.setValue("durationDays", 1);
            this.form.setValue("workingTime", 0);
        }
    }

}
